import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showAlert } from "redux/actions/setAppActions";
import { Spinner } from "components/Spinner";
import { SureAlert } from "components/SureAlert";
import "./SchoolInvitations.css";

export const SchoolInvitations = ({ onInvitationAccepted }) => {
    const { t } = useTranslation("schools");
    const dispatch = useDispatch();
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);
    const [invitationToReject, setInvitationToReject] = useState(null);

    const loadInvitations = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axios.get("invitations");
            setInvitations(data.invitations || []);
        } catch (error) {
            console.error("Error loading invitations:", error);
            dispatch(showAlert({
                type: "error",
                text: error.response?.data?.message || t("load_invitations_error")
            }));
        } finally {
            setLoading(false);
        }
    }, [dispatch, t]);

    useEffect(() => {
        loadInvitations();
    }, [loadInvitations]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        // Конвертируем в МСК (UTC+3)
        const mskDate = new Date(date.getTime() + (3 * 60 * 60 * 1000));
        
        const day = mskDate.getUTCDate().toString().padStart(2, '0');
        const month = (mskDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = mskDate.getUTCFullYear();
        const hours = mskDate.getUTCHours().toString().padStart(2, '0');
        const minutes = mskDate.getUTCMinutes().toString().padStart(2, '0');
        
        return `${day}.${month}.${year} ${hours}:${minutes} МСК`;
    };

    const handleAccept = async (invitationId) => {
        setProcessingId(invitationId);
        try {
            const { data } = await axios.post(`invitations/${invitationId}/accept`);
            dispatch(showAlert({
                type: "success",
                text: data.message || t("invitation_accepted")
            }));
            
            // Удаляем приглашение из списка
            setInvitations(prev => prev.filter(inv => inv._id !== invitationId));
            
            // Вызываем callback для обновления списка школ
            if (onInvitationAccepted) {
                onInvitationAccepted();
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || t("invitation_accept_error");
            dispatch(showAlert({ type: "error", text: errorMessage }));
        } finally {
            setProcessingId(null);
        }
    };

    const handleRejectClick = (invitationId) => {
        setInvitationToReject(invitationId);
    };

    const handleRejectConfirm = async () => {
        if (!invitationToReject) return;

        setProcessingId(invitationToReject);
        try {
            const { data } = await axios.post(`invitations/${invitationToReject}/reject`);
            dispatch(showAlert({
                type: "success",
                text: data.message || t("invitation_rejected")
            }));
            
            // Удаляем приглашение из списка
            setInvitations(prev => prev.filter(inv => inv._id !== invitationToReject));
        } catch (error) {
            const errorMessage = error.response?.data?.message || t("invitation_reject_error");
            dispatch(showAlert({ type: "error", text: errorMessage }));
        } finally {
            setProcessingId(null);
            setInvitationToReject(null);
        }
    };

    const handleRejectCancel = () => {
        setInvitationToReject(null);
    };

    if (loading) {
        return (
            <div className="invitations-loading">
                <Spinner />
            </div>
        );
    }

    if (invitations.length === 0) {
        return null;
    }

    return (
        <div className="school-invitations">
            <h3 className="invitations-title">{t("invitations_title")}</h3>
            
            <div className="invitations-list">
                {invitations.map((invitation) => (
                    <div key={invitation._id} className="invitation-card">
                        <div className="invitation-header">
                            <h4 className="invitation-school-name">
                                {invitation.school?.name || t("unknown_school")}
                            </h4>
                            <span className="invitation-date">
                                {formatDate(invitation.createdAt)}
                            </span>
                        </div>
                        
                        <div className="invitation-info">
                            <p className="invitation-inviter">
                                {t("invited_by")}: {invitation.invitedBy?.surname} {invitation.invitedBy?.name}
                            </p>
                        </div>

                        <div className="invitation-actions">
                            <button
                                className="invitation-btn accept-btn"
                                onClick={() => handleAccept(invitation._id)}
                                disabled={processingId === invitation._id}
                            >
                                {processingId === invitation._id ? t("processing") : t("accept_invitation")}
                            </button>
                            <button
                                className="invitation-btn reject-btn"
                                onClick={() => handleRejectClick(invitation._id)}
                                disabled={processingId === invitation._id}
                            >
                                {t("reject_invitation")}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Модалка подтверждения отклонения */}
            {invitationToReject && (
                <SureAlert
                    title={t("reject_invitation_title")}
                    text={t("invitation_reject_confirm")}
                    handleSubmit={handleRejectConfirm}
                    handleClose={handleRejectCancel}
                    submitText={t("reject_button")}
                />
            )}
        </div>
    );
};
